import { parseISO } from "date-fns";
import type React from "react";
import { useNavigate } from "react-router";

import type { SurveyDetailsV2Dto } from "@/api/types";
import { CountdownBadge } from "@/components/Badge/CountdownBadge";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { CalendarCheck01Icon } from "@/components/Icons/CalendarCheck01Icon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { ClockFastForwardIcon } from "@/components/Icons/ClockFastForwardIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { AutoTranslatedLabel } from "@/components/Label/AutoTranslatedLabel";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { ShareButton } from "@/components/ShareButton/ShareButton";
import { User } from "@/components/User/User";
import { Badge } from "@/design-system/Badge";
import { Divider } from "@/design-system/Divider";
import { Inline } from "@/design-system/Inline";
import { Link } from "@/design-system/Link";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { isContentAutoTranslated } from "@/helpers/languages";
import { getIsSurveySharable } from "@/helpers/surveys";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { surveyMutations } from "@/queries/surveys/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface SurveyInfoSectionProps {
  survey: SurveyDetailsV2Dto;
  showStatus?: boolean;
  showDuration?: boolean;
  showCountdown?: boolean;
}

export function SurveyInfoSection({
  survey,
  showStatus,
  showDuration,
  showCountdown,
}: SurveyInfoSectionProps): React.ReactNode {
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal("survey-delete-modal");

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const group = survey.group;

  const startDate = survey.startsAt ? parseISO(survey.startsAt) : undefined;
  const endDate = survey.endsAt ? parseISO(survey.endsAt) : undefined;
  const canBeShared = getIsSurveySharable(survey);
  const isAutoTranslated = isContentAutoTranslated(survey.originalLanguageId, sessionUser);

  const deleteSurveyMutation = surveyMutations.useDeleteSurvey();

  const menuItems: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      icon: <Edit05Icon />,
      callbackFn: () => navigate(routes.surveys.edit({ slug, id: survey.id })),
      value: "edit",
      isHidden: !survey.canEdit,
      "data-testid": "context-menu-edit-btn",
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      icon: <Trash01Icon />,
      callbackFn: () => openDeleteModal(survey),
      value: "delete",
      isHidden: !survey.canDelete,
      "data-testid": "context-menu-delete-btn",
    },
  ];
  const isBulkSurvey = Boolean(survey.bulkSurveyId);
  const isResponseCountAvailable =
    survey.status === "finished" || (survey.status === "running" && Boolean(survey.answerCount));

  return (
    <>
      <SectionCard dataTestId="survey-details-info-card">
        <Stack gap="sm">
          {/* Header */}
          {showCountdown && survey.endsAt && <CountdownBadge endDate={new Date(survey.endsAt)} />}
          <header className="flex w-full flex-wrap items-center justify-between gap-space-md">
            {survey.createdBy && (
              <User
                user={survey.createdBy}
                renderPostfix={() =>
                  group ? (
                    <Link
                      href={
                        group.type === "helpCategory"
                          ? routes.helpCategories.details({ slug, id: group.id })
                          : routes.interestGroups.details({ slug, id: group.id })
                      }
                      tone="default"
                      underline="hover"
                    >
                      {t("page.survey-details.info.group.in", { groupName: group.name })}
                    </Link>
                  ) : (
                    <Text>
                      {t("page.survey-details.info.group.in", {
                        groupName: t("page.survey-details.info.group.general"),
                      })}
                    </Text>
                  )
                }
              />
            )}
            <nav className="ml-auto flex gap-space-sm">
              <span className="order-first flex w-full flex-wrap items-center gap-space-sm @sm:order-0 @sm:w-auto">
                {survey.isInternal && sessionUser.isSuperAdmin && (
                  <Badge variant="info">{t("page.survey-details.info.internal")}</Badge>
                )}
                {isBulkSurvey && <Badge variant="special">{t("page.survey-details.info.bulk-survey")}</Badge>}
              </span>
              <ContextMenu items={menuItems} />
            </nav>
          </header>
          {/* Content */}
          <Stack gap="sm">
            <p className="text-body-emphasis whitespace-pre-line">{survey.title}</p>
            <MultiLineContent shouldHighlightLinks>{survey.description}</MultiLineContent>
            {canBeShared && (
              <Inline gap="md" justify={isAutoTranslated ? "between" : "end"} wrap={false}>
                {isAutoTranslated && <AutoTranslatedLabel />}
                <ShareButton variant="ghostPrimary" entityId={survey.id} entityType="survey" />
              </Inline>
            )}
          </Stack>
        </Stack>
        <Divider />
        <section className="flex flex-col gap-x-space-md gap-y-space-sm @sm:flex-row @sm:flex-wrap">
          {showDuration && (
            <>
              {startDate && (
                <SurveyInfoBlock
                  label={
                    survey.status === "upcoming"
                      ? t("page.survey-details.info.starts-at")
                      : t("page.survey-details.info.started-at")
                  }
                  value={formatDate(i18n, "datetime", startDate)}
                />
              )}
              {endDate && (
                <SurveyInfoBlock
                  label={
                    survey.status === "upcoming" || survey.status === "running"
                      ? t("page.survey-details.info.ends-at")
                      : t("page.survey-details.info.ended-at")
                  }
                  value={formatDate(i18n, "datetime", endDate)}
                />
              )}
            </>
          )}
          {showStatus && (
            <SurveyInfoBlock label={t("page.survey-details.info.status")}>
              <>
                {survey.status === "finished" && (
                  <Badge icon={<CheckIcon />} variant="info">
                    {t("common.label.ended")}
                  </Badge>
                )}
                {survey.status === "running" && (
                  <Badge icon={<CalendarCheck01Icon />} variant="success">
                    {t("page.survey-details.info.running")}
                  </Badge>
                )}
                {survey.status === "upcoming" && (
                  <Badge icon={<ClockFastForwardIcon />} variant="warning">
                    {t("page.survey-details.info.upcoming")}
                  </Badge>
                )}
              </>
            </SurveyInfoBlock>
          )}
          <SurveyInfoBlock
            label={t("page.survey-details.info.responses-count")}
            value={
              isResponseCountAvailable
                ? t("page.survey-details.info.responses", { count: survey.answerCount! })
                : t("common.label.no-result")
            }
          />
        </section>
      </SectionCard>
      <DeleteModal
        title={t("page.surveys.actions.delete.title")}
        description={t("page.surveys.actions.delete.description")}
        onDelete={() =>
          deleteSurveyMutation.mutateAsync(survey.id, {
            onSuccess: () => {
              void navigate(routes.surveys.list({ slug }));
            },
          })
        }
        deleteBtnProps={{
          "data-testid": "survey-action-delete",
        }}
        {...deleteModalProps}
      />
    </>
  );
}

type SurveyInfoBlock = {
  label: string;
} & (
  | {
      children: React.ReactNode;
      value?: never;
    }
  | {
      children?: never;
      value: string;
    }
);

export function SurveyInfoBlock({ label, children, value }: SurveyInfoBlock): React.ReactNode {
  return (
    <section className="flex min-w-control-narrow flex-col gap-space-xs pr-space-sm">
      <span className="text-caption text-text-secondary">{label}</span>
      <span className="flex min-h-control-sm items-center">
        {children}
        {value && <span className="text-body-emphasis">{value}</span>}
      </span>
    </section>
  );
}
